import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export type SubmissionMethod = 'Manual' | 'Electronic' | '';

export interface Payer {
    id: string;
    name: string;
    payerId: string;
    submissionMethod: SubmissionMethod;
    type: string;
    status: 'Active' | 'Inactive';
    isSystem: boolean;
}

export interface PayerWritePayload {
    name?: string;
    payerId?: string;
    submissionMethod?: SubmissionMethod;
    type?: string;
    status?: 'Active' | 'Inactive';
}

function normalizeStatus(s: string): 'Active' | 'Inactive' {
    return String(s).toLowerCase() === 'active' ? 'Active' : 'Inactive';
}

export function usePayers() {
    return useQuery({
        queryKey: ['payers'] as const,
        queryFn: async () => {
            const res = await api.get<Array<Payer & { status: string }>>('/payers');
            return res.data.map((r) => ({ ...r, status: normalizeStatus(r.status) }));
        },
        staleTime: 60_000,
    });
}

export function useCreatePayer() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: PayerWritePayload) => {
            const res = await api.post<Payer>('/payers', payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['payers'] }),
    });
}

export function useUpdatePayer() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: PayerWritePayload }) => {
            const res = await api.patch<Payer>(`/payers/${id}`, payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['payers'] }),
    });
}

export function useDeletePayer() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/payers/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['payers'] }),
    });
}

export interface BulkSetMethodPayload {
    ids: string[];
    method: 'Manual' | 'Electronic';
}

export interface BulkSetMethodResult {
    /** Number of payer ids in the selection. */
    count: number;
    /** Number actually updated — excludes rows that were already on the target method. */
    flipped: number;
}

// Atomic flip of submission_method across a set of payers. Used by the
// Office Ally opt-in and disable-warning flows — both want "all selected
// payers land on the same method, or none do" semantics.
export function useBulkSetPayerMethod() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: BulkSetMethodPayload) => {
            const res = await api.post<BulkSetMethodResult>('/payers/bulk-set-method', payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['payers'] }),
    });
}
